import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Country, State } from 'country-state-city';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../utils/apiBase';
import TermsModal from '../components/TermsModal';

const LandReporterOnboardingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const countries = useMemo(() => Country.getAllCountries(), []);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    address1: '',
    address2: '',
    zip: '',
    city: '',
    reporterCountry: '',
    reporterRegion: '',
    phone: '',
    isRealEstateBusiness: '',
    companyName: '',
    taxNumber: '',
    acceptTerms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);

  const regions = useMemo(() => {
    if (!formData.reporterCountry) return [];
    const list = State.getStatesOfCountry(formData.reporterCountry);
    return list && list.length > 0 ? list : null;
  }, [formData.reporterCountry]);

  const countryName = useMemo(() => {
    if (!formData.reporterCountry) return '';
    const c = countries.find((x) => x.isoCode === formData.reporterCountry);
    return c ? c.name : '';
  }, [formData.reporterCountry, countries]);

  const openTermsModal = async () => {
    setTermsOpen(true);
    if (termsContent) return;

    setTermsLoading(true);
    try {
      const response = await fetch(apiUrl('/terms?role=land_reporter'));
      const data = await response.json();
      if (response.ok && data.success) {
        setTermsContent(String(data.data?.content || ''));
      }
    } catch (error) {
      console.error('Failed to load terms of service:', error);
    } finally {
      setTermsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData(prev => {
      const next = { ...prev, [name]: nextValue };
      if (name === 'isRealEstateBusiness' && value !== 'yes') {
        next.companyName = '';
        next.taxNumber = '';
      }
      if (name === 'reporterCountry') {
        next.reporterRegion = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/auth/complete-profile'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.middleName ? formData.middleName + ' ' : ''}${formData.lastName}`.trim(),
          address: `${formData.address1}${formData.address2 ? ', ' + formData.address2 : ''}${formData.city ? ', ' + formData.city : ''}${formData.reporterRegion ? ', ' + formData.reporterRegion : ''}${formData.zip ? ' ' + formData.zip : ''}${countryName ? ', ' + countryName : ''}`.trim(),
          phone: formData.phone,
          is_real_estate_business: formData.isRealEstateBusiness === 'yes',
          company_name: formData.isRealEstateBusiness === 'yes' ? formData.companyName : null,
          tax_number: formData.isRealEstateBusiness === 'yes' ? formData.taxNumber : null,
          accept_terms: formData.acceptTerms,
          role: 'land_reporter',
          reporter_country: formData.reporterCountry,
          reporter_region: formData.reporterRegion,
        }),
      });

      const data = await response.json();
      if (data.success) {
        try {
          await refreshUser?.();
        } catch (refreshError) {
          console.error('Failed to refresh user after completing profile:', refreshError);
        }
        navigate('/report');
      } else {
        alert(data.message || t('common.failedToCompleteProfile'));
      }
    } catch (err) {
      console.error('Complete profile error:', err);
      alert(t('common.failedToCompleteProfile'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-4">{t('reporterOnboardingPage.title')}</h1>
        <p className="text-gray-600 mb-6">{t('common.provideDetails')}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.firstName')}</label>
              <input name="firstName" value={formData.firstName} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.middleName')}</label>
              <input name="middleName" value={formData.middleName} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.lastName')}</label>
              <input name="lastName" value={formData.lastName} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.addressLine1')}</label>
            <input name="address1" value={formData.address1} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.addressLine2')}</label>
            <input name="address2" value={formData.address2} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.zip')}</label>
              <input name="zip" value={formData.zip} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.city')}</label>
              <input name="city" value={formData.city} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.country')}</label>
              <select
                name="reporterCountry"
                value={formData.reporterCountry}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">{t('common.select')}</option>
                {countries.map((c) => (
                  <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.region')}</label>
              <select
                name="reporterRegion"
                value={formData.reporterRegion}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md"
                required
                disabled={!formData.reporterCountry}
              >
                <option value="">{t('common.select')}</option>
                {regions ? regions.map((s) => (
                  <option key={s.isoCode} value={s.name}>{s.name}</option>
                )) : (
                  <option value={countryName || formData.reporterCountry}>{t('reporterOnboardingPage.wholeCountry')}</option>
                )}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.phone')}</label>
            <input name="phone" value={formData.phone} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.isRealEstateBusiness')}</label>
              <select
                name="isRealEstateBusiness"
                value={formData.isRealEstateBusiness}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">{t('common.select')}</option>
                <option value="yes">{t('common.yes')}</option>
                <option value="no">{t('common.no')}</option>
              </select>
            </div>
            <div />
          </div>
          {formData.isRealEstateBusiness === 'yes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.companyName')}</label>
                <input name="companyName" value={formData.companyName} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reporterOnboardingPage.taxNumber')}</label>
                <input name="taxNumber" value={formData.taxNumber} onChange={handleChange} className="w-full px-3 py-2 border rounded-md" required />
              </div>
            </div>
          )}
          <div className="flex items-start space-x-2">
            <input id="acceptTerms" type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} className="h-4 w-4" required />
            <label htmlFor="acceptTerms" className="text-sm text-gray-700">
              {t('reporterOnboardingPage.acceptTermsPrefix')}{' '}
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openTermsModal();
                }}
                className="text-blue-600 underline hover:text-blue-800"
              >
                {t('common.termsOfService')}
              </button>
            </label>
          </div>
          <button type="submit" disabled={submitting} className="w-full bg-blue-600 text-white py-2 rounded-md">
            {submitting ? t('reporterOnboardingPage.saving') : t('reporterOnboardingPage.continue')}
          </button>
        </form>
      </div>
      <TermsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        title={t('common.termsOfService')}
        content={termsContent}
        loading={termsLoading}
      />
    </div>
  );
};

export default LandReporterOnboardingPage;
